import { createSelector } from '@reduxjs/toolkit';

import type { AppState } from '../../store/types';
import type { AiConsent, AiDocument, AiFeature, AiState } from './types';

const EMPTY_CONSENTS: AiConsent[] = [];

// Read from merged settings: they carry the domain's billing tier and feature
// flags, so the BE can report a feature the domain is not entitled to as
// UNAVAILABLE. System settings don't, and would report it as available.
export const selectAiState = (state: AppState): AiState | undefined =>
  state.settings.merged.ai ?? undefined;

export const selectIsAiEnabled = (state: AppState): boolean =>
  Boolean(selectAiState(state)?.enabled);

export const selectIsAiSettingsAvailable = (state: AppState): boolean => {
  const ai = selectAiState(state);
  return Boolean(ai && !ai.disabled_by_domain);
};

export const selectAiFeatures = createSelector(
  [selectAiState],
  (ai): AiFeature[] =>
    Object.entries(ai?.features ?? {})
      // No plan grants it (e.g. a beta behind a domain flag), so a "not
      // available for your plan" row would mislead.
      .filter(
        ([, feature]) =>
          feature.status !== 'UNAVAILABLE' || feature.billing_tiers?.length,
      )
      .map(([key, feature]) => ({ ...feature, key })),
);

export const selectAiDocuments = (state: AppState): AiDocument[] =>
  state.aiSettings.documents;

const selectAiConsents = (state: AppState): AiConsent[] =>
  selectAiState(state)?.consents ?? EMPTY_CONSENTS;

export const selectAiConsentsForDocument = createSelector(
  [selectAiConsents, (_state: AppState, documentId: string) => documentId],
  (consents, documentId): AiConsent[] =>
    consents.filter((consent) => consent.document_id === documentId),
);

export const selectAiConsentForDocument = (
  state: AppState,
  documentId: string,
  version: string,
): AiConsent | undefined =>
  selectAiConsents(state).find(
    (consent) =>
      consent.document_id === documentId && consent.version === version,
  );
